import React, { useState, useEffect } from 'react';
import {
   Container,
   Box,
   Typography,
   Tabs,
   Tab,
   Card,
   CardContent,
   Button,
   Avatar,
   Chip,
   Grid,
   CircularProgress,
   Alert,
   Divider,
   IconButton,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   useMediaQuery,
   useTheme,
} from '@mui/material';
import {
   VideoCall as VideoCallIcon,
   Schedule as ScheduleIcon,
   Person as PersonIcon,
   Launch as LaunchIcon,
   Check as AcceptIcon,
   Close as DeclineIcon,
   Cancel as CancelIcon,
   Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, formatDistance, isPast, isFuture } from 'date-fns';
import { useUser } from '../../contexts/UserContext';
import { useScheduledCallVideo } from '../../contexts/ScheduledCallVideoContext';
import {
   collection,
   query,
   where,
   onSnapshot,
   orderBy,
   doc,
   updateDoc,
   getDoc,
   deleteDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';

interface ScheduledCall {
   id: string;
   title: string;
   description?: string;
   startDate: Date;
   endDate: Date;
   organizerId: string;
   organizerName: string;
   organizerEmail: string;
   participants: Array<{
      userId: string;
      email: string;
      displayName: string;
      photoURL?: string;
      status: 'pending' | 'accepted' | 'declined';
      role: 'organizer' | 'participant';
   }>;
   status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
   virtualRoomSid?: string;
   virtualRoomName?: string;
   virtualRoomUrl?: string;
   joinUrl?: string;
   createdAt: Date;
}

interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

function TabPanel(props: TabPanelProps) {
   const { children, value, index, ...other } = props;

   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`scheduled-calls-tabpanel-${index}`}
         aria-labelledby={`scheduled-calls-tab-${index}`}
         {...other}
      >
         {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
      </div>
   );
}

const ScheduledCallsPage: React.FC = () => {
   const { currentUser } = useUser();
   const { startScheduledCall, videoCallState } = useScheduledCallVideo();
   const navigate = useNavigate();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   const [tabValue, setTabValue] = useState(0);
   const [scheduledCalls, setScheduledCalls] = useState<ScheduledCall[]>([]);
   const [receivedCalls, setReceivedCalls] = useState<ScheduledCall[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [processingCall, setProcessingCall] = useState<string | null>(null);
   const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
   const [callToCancel, setCallToCancel] = useState<ScheduledCall | null>(null);
   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [callToDelete, setCallToDelete] = useState<ScheduledCall | null>(null);
   const [wasInCall, setWasInCall] = useState(false);

   // Monitor video call state to redirect when call ends
   useEffect(() => {
      if (videoCallState.isActive && !wasInCall) {
         setWasInCall(true);
      } else if (!videoCallState.isActive && wasInCall) {
         // Call just ended, redirect back to dashboard
         setWasInCall(false);
         // Only redirect if we're not already on the dashboard page
         if (window.location.pathname !== '/dashboard') {
            navigate('/dashboard');
         }
      }
   }, [videoCallState.isActive, wasInCall, navigate]);

   useEffect(() => {
      console.log('ScheduledCalls useEffect triggered, currentUser:', currentUser?.uid);

      // Reset error state on mount/remount
      setError(null);
      setLoading(true);

      if (!currentUser) {
         console.log('No current user, setting empty arrays');
         setScheduledCalls([]);
         setReceivedCalls([]);
         setLoading(false);
         return;
      }

      // Query for calls the user organized
      const scheduledQuery = query(
         collection(db, 'scheduledCalls'),
         where('organizerId', '==', currentUser.uid)
      );

      // Query for calls the user was invited to
      const receivedQuery = query(
         collection(db, 'scheduledCalls'),
         where('invitedUser', '==', currentUser.uid)
      );

      const unsubscribeScheduled = onSnapshot(
         scheduledQuery,
         (snapshot) => {
            console.log(
               '🔄 ORGANIZER: Scheduled calls snapshot received:',
               snapshot.size,
               'documents'
            );
            console.log(
               '🔄 ORGANIZER: Snapshot changes:',
               snapshot.docChanges().map((change) => ({
                  type: change.type,
                  id: change.doc.id,
                  data: change.doc.data(),
               }))
            );
            try {
               const callData: ScheduledCall[] = [];
               snapshot.forEach((doc) => {
                  try {
                     const data = doc.data();
                     console.log('Processing scheduled call:', doc.id, data);
                     callData.push({
                        id: doc.id,
                        title: data.title || 'Untitled Call',
                        description: data.description,
                        startDate: data.startDate?.toDate() || new Date(),
                        endDate: data.endDate?.toDate() || new Date(),
                        organizerId: data.organizerId,
                        organizerName: data.organizerName || 'Unknown',
                        organizerEmail: data.organizerEmail || '',
                        participants: data.participants || [],
                        status: data.status || 'scheduled',
                        virtualRoomSid: data.virtualRoomSid,
                        virtualRoomName: data.virtualRoomName,
                        virtualRoomUrl: data.virtualRoomUrl,
                        joinUrl: data.joinUrl,
                        createdAt: data.createdAt?.toDate() || new Date(),
                     });
                  } catch (docError) {
                     console.error('Error processing document:', doc.id, docError);
                  }
               });
               setScheduledCalls(callData);
               setLoading(false);
               setError(null); // Clear any previous errors
            } catch (snapshotError) {
               console.error('Error processing snapshot:', snapshotError);
               setError('Failed to process scheduled calls data');
               setLoading(false);
            }
         },
         (error) => {
            console.error('Error in scheduled calls query:', error);
            setError('Failed to load scheduled calls: ' + error.message);
            setLoading(false);
         }
      );

      const unsubscribeReceived = onSnapshot(
         receivedQuery,
         (snapshot) => {
            console.log(
               '🔄 INVITEE: Received calls snapshot received:',
               snapshot.size,
               'documents'
            );
            console.log(
               '🔄 INVITEE: Snapshot changes:',
               snapshot.docChanges().map((change) => ({
                  type: change.type,
                  id: change.doc.id,
                  data: change.doc.data(),
               }))
            );
            try {
               const callData: ScheduledCall[] = [];
               snapshot.forEach((doc) => {
                  try {
                     const data = doc.data();
                     console.log('Processing received call:', doc.id, data);
                     callData.push({
                        id: doc.id,
                        title: data.title || 'Untitled Call',
                        description: data.description,
                        startDate: data.startDate?.toDate() || new Date(),
                        endDate: data.endDate?.toDate() || new Date(),
                        organizerId: data.organizerId,
                        organizerName: data.organizerName || 'Unknown',
                        organizerEmail: data.organizerEmail || '',
                        participants: data.participants || [],
                        status: data.status || 'scheduled',
                        virtualRoomSid: data.virtualRoomSid,
                        virtualRoomName: data.virtualRoomName,
                        virtualRoomUrl: data.virtualRoomUrl,
                        joinUrl: data.joinUrl,
                        createdAt: data.createdAt?.toDate() || new Date(),
                     });
                  } catch (docError) {
                     console.error('Error processing received call document:', doc.id, docError);
                  }
               });
               setReceivedCalls(callData);
            } catch (snapshotError) {
               console.error('Error processing received calls snapshot:', snapshotError);
               setError('Failed to process received calls data');
            }
         },
         (error) => {
            console.error('Error in received calls query:', error);
            setError('Failed to load received calls: ' + error.message);
         }
      );

      return () => {
         unsubscribeScheduled();
         unsubscribeReceived();
      };
   }, [currentUser]);

   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
   };

   const getCallStatus = (call: ScheduledCall, isOrganizer: boolean = false) => {
      const now = new Date();
      const startTime = call.startDate;
      const endTime = call.endDate;

      if (call.status === 'cancelled') {
         return { label: 'Cancelled', color: 'error' as const };
      }

      if (call.status === 'completed') {
         return { label: 'Completed', color: 'success' as const };
      }

      if (now >= startTime && now <= endTime) {
         return { label: 'Live Now', color: 'success' as const };
      }

      // For organizers, show if the invitee has responded (but only for future calls)
      if (isOrganizer && isFuture(startTime)) {
         const invitee = call.participants.find((p) => p.role === 'participant');
         if (invitee?.status === 'pending') {
            return { label: 'Pending Response', color: 'warning' as const };
         } else if (invitee?.status === 'accepted') {
            return { label: 'Accepted', color: 'success' as const };
         } else if (invitee?.status === 'declined') {
            return { label: 'Declined', color: 'error' as const };
         }
      }

      // For invitees, show their own response status (but only for future calls)
      if (!isOrganizer && isFuture(startTime)) {
         const currentUserParticipant = call.participants.find(
            (p) => p.userId === currentUser?.uid
         );
         if (currentUserParticipant?.status === 'accepted') {
            return { label: 'Accepted', color: 'success' as const };
         } else if (currentUserParticipant?.status === 'declined') {
            return { label: 'Declined', color: 'error' as const };
         } else if (currentUserParticipant?.status === 'pending') {
            return { label: 'Pending', color: 'warning' as const };
         }
      }

      // For past calls that weren't marked as completed
      if (isPast(endTime)) {
         return { label: 'Ended', color: 'default' as const };
      }

      return { label: 'Scheduled', color: 'primary' as const };
   };

   const getUserParticipantStatus = (call: ScheduledCall) => {
      const participant = call.participants.find((p) => p.userId === currentUser?.uid);
      return participant?.status || 'pending';
   };

   const getOtherParticipant = (call: ScheduledCall) => {
      return call.participants.find((p) => p.userId !== currentUser?.uid);
   };

   const handleJoinCall = (call: ScheduledCall) => {
      console.log('DEBUG: handleJoinCall called for:', call.title);

      if (!call.id) {
         setError('Unable to join call. Missing call information.');
         return;
      }

      // Navigate to the scheduled call room page (same pattern as virtual events)
      navigate(`/scheduled-call-room/${call.id}`);
   };

   const handleAcceptCall = async (call: ScheduledCall) => {
      if (!currentUser) return;

      try {
         setProcessingCall(call.id);
         setError(null);

         const eventRef = doc(db, 'scheduledCalls', call.id);
         const eventDoc = await getDoc(eventRef);

         if (eventDoc.exists()) {
            const eventData = eventDoc.data();
            const updatedParticipants = eventData.participants.map((p: any) =>
               p.userId === currentUser.uid ? { ...p, status: 'accepted' } : p
            );

            await updateDoc(eventRef, {
               participants: updatedParticipants,
               updatedAt: new Date(),
            });

            console.log(
               '✅ ACCEPT: Updated Firestore document with accepted status for user:',
               currentUser.uid
            );

            // Send notification to organizer
            try {
               const headers = await getAuthHeaders();
               await axios.post(
                  `${process.env.REACT_APP_API_URL}/api/notifications/send-event-response`,
                  {
                     eventId: call.id,
                     organizerUserId: call.organizerId,
                     respondentName: currentUser.displayName || 'Someone',
                     eventTitle: call.title,
                     response: 'accepted',
                     eventType: 'virtual_call',
                  },
                  { headers }
               );
            } catch (notificationError) {
               console.error('Error sending acceptance notification:', notificationError);
            }
         }
      } catch (error) {
         console.error('Error accepting call:', error);
         setError('Failed to accept call. Please try again.');
      } finally {
         setProcessingCall(null);
      }
   };

   const handleDeclineCall = async (call: ScheduledCall) => {
      if (!currentUser) return;

      try {
         setProcessingCall(call.id);
         setError(null);

         const eventRef = doc(db, 'scheduledCalls', call.id);
         const eventDoc = await getDoc(eventRef);

         if (eventDoc.exists()) {
            const eventData = eventDoc.data();
            const updatedParticipants = eventData.participants.map((p: any) =>
               p.userId === currentUser.uid ? { ...p, status: 'declined' } : p
            );

            await updateDoc(eventRef, {
               participants: updatedParticipants,
               updatedAt: new Date(),
            });

            console.log(
               '✅ DECLINE: Updated Firestore document with declined status for user:',
               currentUser.uid
            );

            // Send notification to organizer
            try {
               const headers = await getAuthHeaders();
               await axios.post(
                  `${process.env.REACT_APP_API_URL}/api/notifications/send-event-response`,
                  {
                     eventId: call.id,
                     organizerUserId: call.organizerId,
                     respondentName: currentUser.displayName || 'Someone',
                     eventTitle: call.title,
                     response: 'declined',
                     eventType: 'virtual_call',
                  },
                  { headers }
               );
            } catch (notificationError) {
               console.error('Error sending decline notification:', notificationError);
            }
         }
      } catch (error) {
         console.error('Error declining call:', error);
         setError('Failed to decline call. Please try again.');
      } finally {
         setProcessingCall(null);
      }
   };

   const handleCancelCall = async (call: ScheduledCall) => {
      if (!currentUser) return;

      try {
         setProcessingCall(call.id);
         setError(null);

         const eventRef = doc(db, 'scheduledCalls', call.id);
         await updateDoc(eventRef, {
            status: 'cancelled',
            updatedAt: new Date(),
         });

         console.log(
            '✅ CANCEL: Updated Firestore document with cancelled status for call:',
            call.id
         );

         // Send notification to participants
         const otherParticipant = getOtherParticipant(call);
         if (otherParticipant) {
            try {
               const headers = await getAuthHeaders();
               await axios.post(
                  `${process.env.REACT_APP_API_URL}/api/notifications/send-event-response`,
                  {
                     eventId: call.id,
                     organizerUserId: otherParticipant.userId,
                     respondentName: currentUser.displayName || 'Someone',
                     eventTitle: call.title,
                     response: 'cancelled',
                     eventType: 'virtual_call',
                  },
                  { headers }
               );
            } catch (notificationError) {
               console.error('Error sending cancellation notification:', notificationError);
            }
         }

         setCancelDialogOpen(false);
         setCallToCancel(null);
      } catch (error) {
         console.error('Error cancelling call:', error);
         setError('Failed to cancel call. Please try again.');
      } finally {
         setProcessingCall(null);
      }
   };

   const handleDeleteCall = async (call: ScheduledCall) => {
      if (!currentUser) return;

      try {
         setProcessingCall(call.id);
         setError(null);

         const eventRef = doc(db, 'scheduledCalls', call.id);
         await deleteDoc(eventRef);

         console.log('✅ DELETE: Deleted Firestore document for call:', call.id);

         // Send notification to participants if call was scheduled
         if (call.status === 'scheduled') {
            const otherParticipant = getOtherParticipant(call);
            if (otherParticipant) {
               try {
                  const headers = await getAuthHeaders();
                  await axios.post(
                     `${process.env.REACT_APP_API_URL}/api/notifications/send-event-response`,
                     {
                        eventId: call.id,
                        organizerUserId: otherParticipant.userId,
                        respondentName: currentUser.displayName || 'Someone',
                        eventTitle: call.title,
                        response: 'deleted',
                        eventType: 'virtual_call',
                     },
                     { headers }
                  );
               } catch (notificationError) {
                  console.error('Error sending deletion notification:', notificationError);
               }
            }
         }

         setDeleteDialogOpen(false);
         setCallToDelete(null);
      } catch (error) {
         console.error('Error deleting call:', error);
         setError('Failed to delete call. Please try again.');
      } finally {
         setProcessingCall(null);
      }
   };

   const renderCallCard = (call: ScheduledCall, isOrganizer: boolean) => {
      const status = getCallStatus(call, isOrganizer);
      const userStatus = getUserParticipantStatus(call);
      const otherParticipant = getOtherParticipant(call);

      // Debug logging
      console.log('Rendering call card:', {
         callId: call.id,
         callTitle: call.title,
         isOrganizer,
         status: status.label,
         userStatus,
         otherParticipant: otherParticipant?.displayName,
         otherParticipantStatus: otherParticipant?.status,
         participants: call.participants,
      });
      // Both organizer and invitee can join if:
      // 1. Call has Twilio room info and is not cancelled
      // 2. For organizer: can always join (they created the call)
      // 3. For invitee: can join once they accept
      const inviteeAccepted =
         call.participants.find((p) => p.role === 'participant')?.status === 'accepted';

      let canJoin = false;
      if (call.virtualRoomSid && call.virtualRoomName && call.status !== 'cancelled') {
         if (isOrganizer) {
            // Organizer can always join their own call
            canJoin = true;
         } else {
            // Invitee can only join if they've accepted
            canJoin = userStatus === 'accepted';
         }
      }
      const canCancel = isOrganizer && call.status === 'scheduled' && isFuture(call.startDate);
      const canDelete = isOrganizer; // Organizer can delete call at any time

      // For organizers: don't show join/cancel if invitee declined, call cancelled, completed, or ended
      const inviteeDeclined =
         call.participants.find((p) => p.role === 'participant')?.status === 'declined';
      const callEnded = isPast(call.endDate);
      const shouldHideOrganizerButtons =
         isOrganizer &&
         (inviteeDeclined ||
            call.status === 'cancelled' ||
            call.status === 'completed' ||
            callEnded);

      // For invitees: don't show buttons if they declined, call cancelled, completed, or ended
      const shouldHideInviteeButtons =
         !isOrganizer &&
         (userStatus === 'declined' ||
            call.status === 'cancelled' ||
            call.status === 'completed' ||
            callEnded);

      console.log('DEBUG: canJoin calculation:', {
         callId: call.id,
         callTitle: call.title,
         isOrganizer,
         userStatus,
         hasRoomInfo: !!(call.virtualRoomSid && call.virtualRoomName),
         isNotCancelled: call.status !== 'cancelled',
         virtualRoomSid: call.virtualRoomSid,
         virtualRoomName: call.virtualRoomName,
         callStatus: call.status,
         canJoin,
         inviteeDeclined,
         shouldHideOrganizerButtons,
      });

      return (
         <Grid item xs={12} sm={6} md={4} key={call.id}>
            <Card
               sx={{
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                     transform: 'translateY(-2px)',
                     boxShadow: 3,
                  },
                  ...(status.label === 'Live Now' && {
                     border: '2px solid',
                     borderColor: 'success.main',
                  }),
               }}
            >
               <CardContent sx={{ flex: 1 }}>
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2,
                     }}
                  >
                     <Typography variant='h6' noWrap sx={{ flex: 1, mr: 1 }}>
                        {call.title}
                     </Typography>
                     <Chip
                        label={status.label}
                        color={status.color}
                        size='small'
                        variant={status.label === 'Live Now' ? 'filled' : 'outlined'}
                     />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                     <ScheduleIcon fontSize='small' color='action' />
                     <Typography variant='body2' color='text.secondary'>
                        {format(call.startDate, 'MMM d, yyyy • h:mm a')}
                     </Typography>
                  </Box>

                  {call.description && (
                     <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        {call.description}
                     </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                     <Avatar sx={{ width: 32, height: 32 }}>
                        <PersonIcon />
                     </Avatar>
                     <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='body2' fontWeight='medium' noWrap>
                           {isOrganizer
                              ? `With: ${otherParticipant?.displayName || 'Unknown'}`
                              : `Host: ${call.organizerName}`}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                           {formatDistance(call.startDate, new Date(), { addSuffix: true })}
                        </Typography>
                     </Box>
                  </Box>

                  {/* Status Info */}
                  {call.status === 'cancelled' && (
                     <Alert severity='error' sx={{ mb: 2, fontSize: '0.8rem' }}>
                        This call has been cancelled
                     </Alert>
                  )}
                  {call.status === 'completed' && (
                     <Alert severity='info' sx={{ mb: 2, fontSize: '0.8rem' }}>
                        This call has ended
                     </Alert>
                  )}
                  {call.status === 'scheduled' &&
                     isOrganizer &&
                     status.label === 'Pending Response' && (
                        <Alert severity='info' sx={{ mb: 2, fontSize: '0.8rem' }}>
                           Waiting for {otherParticipant?.displayName || 'participant'} to respond
                        </Alert>
                     )}
                  {call.status === 'scheduled' && isOrganizer && status.label === 'Accepted' && (
                     <Alert severity='success' sx={{ mb: 2, fontSize: '0.8rem' }}>
                        {otherParticipant?.displayName || 'Participant'} accepted this call
                     </Alert>
                  )}
                  {call.status === 'scheduled' && isOrganizer && status.label === 'Declined' && (
                     <Alert severity='error' sx={{ mb: 2, fontSize: '0.8rem' }}>
                        {otherParticipant?.displayName || 'Participant'} declined this call
                     </Alert>
                  )}
                  {call.status === 'scheduled' && !isOrganizer && userStatus === 'pending' && (
                     <Alert severity='warning' sx={{ mb: 2, fontSize: '0.8rem' }}>
                        Please respond to this call invitation
                     </Alert>
                  )}
                  {call.status === 'scheduled' && !isOrganizer && userStatus === 'accepted' && (
                     <Alert severity='success' sx={{ mb: 2, fontSize: '0.8rem' }}>
                        You accepted this call invitation
                     </Alert>
                  )}
                  {call.status === 'scheduled' && !isOrganizer && userStatus === 'declined' && (
                     <Alert severity='error' sx={{ mb: 2, fontSize: '0.8rem' }}>
                        You declined this call invitation
                     </Alert>
                  )}
                  {(status.label === 'Ended' ||
                     (isPast(call.endDate) && call.status === 'scheduled')) && (
                     <Alert severity='info' sx={{ mb: 2, fontSize: '0.8rem' }}>
                        This call has ended
                     </Alert>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                     {isOrganizer ? (
                        // Organizer actions
                        <>
                           {canJoin && !shouldHideOrganizerButtons && (
                              <Button
                                 variant={status.label === 'Live Now' ? 'contained' : 'outlined'}
                                 color={status.label === 'Live Now' ? 'success' : 'primary'}
                                 size='small'
                                 startIcon={
                                    status.label === 'Live Now' ? <VideoCallIcon /> : <LaunchIcon />
                                 }
                                 onClick={() => handleJoinCall(call)}
                                 sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    px: 2,
                                    py: 0.5,
                                 }}
                              >
                                 {status.label === 'Live Now' ? 'Join' : 'Join'}
                              </Button>
                           )}

                           {canCancel && !shouldHideOrganizerButtons && (
                              <Button
                                 variant='outlined'
                                 color='error'
                                 size='small'
                                 startIcon={
                                    processingCall === call.id ? (
                                       <CircularProgress size={14} />
                                    ) : (
                                       <CancelIcon />
                                    )
                                 }
                                 onClick={() => {
                                    setCallToCancel(call);
                                    setCancelDialogOpen(true);
                                 }}
                                 disabled={processingCall === call.id}
                                 sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    px: 2,
                                    py: 0.5,
                                 }}
                              >
                                 Cancel
                              </Button>
                           )}

                           {canDelete && (
                              <Button
                                 variant='outlined'
                                 color='error'
                                 size='small'
                                 startIcon={
                                    processingCall === call.id ? (
                                       <CircularProgress size={14} />
                                    ) : (
                                       <DeleteIcon />
                                    )
                                 }
                                 onClick={() => {
                                    setCallToDelete(call);
                                    setDeleteDialogOpen(true);
                                 }}
                                 disabled={processingCall === call.id}
                                 sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    px: 2,
                                    py: 0.5,
                                 }}
                              >
                                 Delete
                              </Button>
                           )}
                        </>
                     ) : (
                        // Invitee actions - only show if not hidden
                        <>
                           {!shouldHideInviteeButtons &&
                              userStatus === 'pending' &&
                              call.status === 'scheduled' && (
                                 <>
                                    <Button
                                       variant='contained'
                                       color='success'
                                       size='small'
                                       startIcon={
                                          processingCall === call.id ? (
                                             <CircularProgress size={14} />
                                          ) : (
                                             <AcceptIcon />
                                          )
                                       }
                                       onClick={() => handleAcceptCall(call)}
                                       disabled={processingCall === call.id}
                                       sx={{
                                          fontSize: '0.75rem',
                                          fontWeight: 500,
                                          textTransform: 'none',
                                          px: 2,
                                          py: 0.5,
                                       }}
                                    >
                                       Accept
                                    </Button>
                                    <Button
                                       variant='outlined'
                                       color='error'
                                       size='small'
                                       startIcon={
                                          processingCall === call.id ? (
                                             <CircularProgress size={14} />
                                          ) : (
                                             <DeclineIcon />
                                          )
                                       }
                                       onClick={() => handleDeclineCall(call)}
                                       disabled={processingCall === call.id}
                                       sx={{
                                          fontSize: '0.75rem',
                                          fontWeight: 500,
                                          textTransform: 'none',
                                          px: 2,
                                          py: 0.5,
                                       }}
                                    >
                                       Decline
                                    </Button>
                                 </>
                              )}
                           {!shouldHideInviteeButtons && userStatus === 'accepted' && canJoin && (
                              <Button
                                 variant={status.label === 'Live Now' ? 'contained' : 'outlined'}
                                 color={status.label === 'Live Now' ? 'success' : 'primary'}
                                 size='small'
                                 startIcon={
                                    status.label === 'Live Now' ? <VideoCallIcon /> : <LaunchIcon />
                                 }
                                 onClick={() => {
                                    console.log(
                                       'DEBUG: Join button clicked for invitee:',
                                       call.title
                                    );
                                    handleJoinCall(call);
                                 }}
                                 sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    px: 2,
                                    py: 0.5,
                                 }}
                              >
                                 {status.label === 'Live Now' ? 'Join' : 'Join'}
                              </Button>
                           )}
                        </>
                     )}
                  </Box>
               </CardContent>
            </Card>
         </Grid>
      );
   };

   if (loading) {
      return (
         <Container maxWidth='lg' sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
               <CircularProgress />
            </Box>
         </Container>
      );
   }

   return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
         <Box sx={{ mb: 4 }}>
            <Typography
               variant='h4'
               gutterBottom
               sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
            >
               <VideoCallIcon color='primary' fontSize='large' />
               Scheduled Calls
            </Typography>
            <Typography variant='body1' color='text.secondary'>
               Manage your virtual call invitations and scheduled meetings
            </Typography>
         </Box>

         {error && (
            <Alert severity='error' sx={{ mb: 3 }}>
               {error}
            </Alert>
         )}

         <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
               <Tab
                  label={`My Scheduled Calls (${scheduledCalls.length})`}
                  id='scheduled-calls-tab-0'
                  aria-controls='scheduled-calls-tabpanel-0'
               />
               <Tab
                  label={`Received Invitations (${receivedCalls.length})`}
                  id='scheduled-calls-tab-1'
                  aria-controls='scheduled-calls-tabpanel-1'
               />
            </Tabs>
         </Box>

         <TabPanel value={tabValue} index={0}>
            {scheduledCalls.length === 0 ? (
               <Box sx={{ textAlign: 'center', py: 8 }}>
                  <VideoCallIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant='h6' color='text.secondary' gutterBottom>
                     No scheduled calls
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                     Schedule a call with someone from your network to get started
                  </Typography>
               </Box>
            ) : (
               <Grid container spacing={3}>
                  {scheduledCalls.map((call) => renderCallCard(call, true))}
               </Grid>
            )}
         </TabPanel>

         <TabPanel value={tabValue} index={1}>
            {receivedCalls.length === 0 ? (
               <Box sx={{ textAlign: 'center', py: 8 }}>
                  <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant='h6' color='text.secondary' gutterBottom>
                     No call invitations
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                     Call invitations from others will appear here
                  </Typography>
               </Box>
            ) : (
               <Grid container spacing={3}>
                  {receivedCalls.map((call) => renderCallCard(call, false))}
               </Grid>
            )}
         </TabPanel>

         {/* Cancel Confirmation Dialog */}
         <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
            <DialogTitle>Cancel Call</DialogTitle>
            <DialogContent>
               <Typography>
                  Are you sure you want to cancel "{callToCancel?.title}"? This action cannot be
                  undone and all participants will be notified.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setCancelDialogOpen(false)}>Keep Call</Button>
               <Button
                  onClick={() => callToCancel && handleCancelCall(callToCancel)}
                  color='error'
                  disabled={processingCall === callToCancel?.id}
               >
                  {processingCall === callToCancel?.id ? 'Cancelling...' : 'Cancel Call'}
               </Button>
            </DialogActions>
         </Dialog>

         <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Delete Call</DialogTitle>
            <DialogContent>
               <Typography>
                  Are you sure you want to permanently delete "{callToDelete?.title}"? This action
                  cannot be undone and the call will be removed from all participants' schedules.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setDeleteDialogOpen(false)}>Keep Call</Button>
               <Button
                  onClick={() => callToDelete && handleDeleteCall(callToDelete)}
                  color='error'
                  disabled={processingCall === callToDelete?.id}
               >
                  {processingCall === callToDelete?.id ? 'Deleting...' : 'Delete Call'}
               </Button>
            </DialogActions>
         </Dialog>
      </Container>
   );
};

export default ScheduledCallsPage;
